// Resource Center articles. The idea (straight from Gabe's feedback): every
// question he answers three times becomes a page. Each article is a list of
// typed blocks so one renderer handles all of them. Add an entry here and it
// shows up in the Resource Center index and gets its own route.
//
// Block types the renderer understands:
//   { type: 'p', text }                         paragraph
//   { type: 'h', text }                          subheading
//   { type: 'list', items: [] }                  bulleted list
//   { type: 'callout', text }                    highlighted pull-quote
//   { type: 'columns', columns: [{title, subtitle, items:[]}] }  side-by-side cards

export const resourceCategories = {
	equipment: 'Equipment',
	incentives: 'Incentives & Savings',
	process: 'How Solar Works',
};

export const resources = [
	{
		id: 'powerwall-vs-enphase',
		category: 'equipment',
		title: 'Tesla Powerwall 3 vs Enphase Battery Storage',
		excerpt:
			'One of the most common questions I get: "What battery should I install?" The honest answer is — it depends on your goals. Here is how I think about it.',
		readingTime: '4 min',
		blocks: [
			{
				type: 'p',
				text: 'One of the most common questions I receive is: "What battery should I install?"',
			},
			{ type: 'p', text: 'The answer depends on your goals.' },
			{
				type: 'columns',
				columns: [
					{
						title: 'Tesla Powerwall 3',
						subtitle: 'Best for',
						items: [
							'High power output',
							'Whole-home backup',
							'Large loads',
							'Cost effectiveness',
						],
						advantages: [
							'Powerful inverter',
							'Large storage capacity',
							'Excellent value',
						],
					},
					{
						title: 'Enphase IQ Battery',
						subtitle: 'Best for',
						items: [
							'Existing Enphase systems',
							'Modular expansion',
							'Flexibility',
							'Component-level redundancy',
						],
						advantages: [
							'Expandable',
							'Integrated with the Enphase ecosystem',
							'Excellent monitoring',
						],
					},
				],
			},
			{ type: 'h', text: 'Which would I choose?' },
			{ type: 'p', text: 'Every home is different.' },
			{
				type: 'p',
				text: 'I help homeowners evaluate their goals, loads, budget, and future expansion plans before making a recommendation.',
			},
		],
	},
	{
		id: 'rec-vs-canadian-solar',
		category: 'equipment',
		title: 'REC vs Canadian Solar Panels',
		excerpt:
			'Both make quality panels. But when homeowners ask what I would put on my own roof, I generally lean toward REC. Here is why.',
		readingTime: '3 min',
		blocks: [
			{
				type: 'p',
				text: 'Both REC and Canadian Solar manufacture quality solar panels.',
			},
			{
				type: 'p',
				text: 'However, when homeowners ask what I would install on my own home, I generally lean toward REC.',
			},
			{ type: 'h', text: 'Why?' },
			{ type: 'p', text: 'REC panels typically offer:' },
			{
				type: 'list',
				items: [
					'Higher efficiency',
					'Lower degradation rates',
					'Stronger temperature performance',
					'Industry-leading warranties',
				],
			},
			{
				type: 'p',
				text: 'Canadian Solar can be a great value option and may make sense in certain situations.',
			},
			{ type: 'callout', text: 'The best panel isn’t always the cheapest panel.' },
			{
				type: 'p',
				text: 'The best panel is the one that delivers the highest long-term value.',
			},
		],
	},
	{
		id: 'maryland-solar-incentives',
		category: 'incentives',
		title: 'Maryland Solar Incentives',
		excerpt:
			'Maryland remains one of the best states in the country for residential solar. Here are the incentives that can apply to your project.',
		readingTime: '4 min',
		// Mirrors the standalone /maryland-solar-incentives page so it also lives
		// inside the Resource Center index.
		blocks: [
			{
				type: 'p',
				text: 'Maryland continues to be one of the best states in the country for residential solar.',
			},
			{ type: 'p', text: 'Available incentives may include:' },
			{ type: 'h', text: 'Federal Tax Credit' },
			{
				type: 'p',
				text: 'Eligible homeowners may qualify for federal tax incentives when purchasing a solar system.',
			},
			{ type: 'h', text: 'SRECs' },
			{
				type: 'p',
				text: 'Solar Renewable Energy Credits allow homeowners to earn additional income based on solar production.',
			},
			{ type: 'h', text: 'Net Metering' },
			{
				type: 'p',
				text: 'Maryland homeowners can receive utility bill credits for excess electricity sent to the grid.',
			},
			{ type: 'h', text: 'Battery Incentives' },
			{
				type: 'p',
				text: 'Additional programs and incentives may periodically become available for battery storage systems.',
			},
			{
				type: 'callout',
				text: 'I help homeowners understand which incentives they qualify for and how to maximize their savings.',
			},
		],
	},
	{
		id: 'how-net-metering-works',
		category: 'incentives',
		title: 'How Net Metering Works in Maryland',
		excerpt:
			'When your panels make more power than you use, that surplus goes to the grid and your utility credits it back. Here is the plain-English version.',
		readingTime: '3 min',
		blocks: [
			{
				type: 'p',
				text: 'Net metering is the program that makes rooftop solar pencil out for most Maryland homeowners.',
			},
			{
				type: 'p',
				text: 'When your solar system produces more electricity than your home is using, the excess energy is sent back to the grid.',
			},
			{
				type: 'p',
				text: 'Your utility provides credits for that excess production through Maryland’s net-metering program.',
			},
			{
				type: 'p',
				text: 'These credits can offset electricity you use at night or during lower-production periods — so your panels effectively cover usage even when the sun isn’t out.',
			},
			{ type: 'h', text: 'Why it matters' },
			{
				type: 'list',
				items: [
					'You get value for every kWh you over-produce',
					'Credits roll forward to cover low-production months',
					'It protects you from a chunk of future rate increases',
				],
			},
		],
	},
	{
		id: 'how-solar-works-with-gabe',
		category: 'process',
		title: 'The Solar Process, Step by Step',
		excerpt:
			'From the first bill review to Permission to Operate, here is exactly what happens — and where I personally handle things for you.',
		readingTime: '4 min',
		blocks: [
			{
				type: 'p',
				text: 'Going solar can feel like a black box. It isn’t. Here is every step, in order.',
			},
			{ type: 'h', text: 'Step 1 — Initial Consultation' },
			{ type: 'p', text: 'We review your electric bills, goals, and property.' },
			{ type: 'h', text: 'Step 2 — Site Assessment' },
			{ type: 'p', text: 'I analyze your roof, shading, and solar potential.' },
			{ type: 'h', text: 'Step 3 — Design & Engineering' },
			{
				type: 'p',
				text: 'Our engineering team creates a custom system design.',
			},
			{ type: 'h', text: 'Step 4 — Permits & Utility Approvals' },
			{ type: 'p', text: 'We handle the paperwork and approvals.' },
			{ type: 'h', text: 'Step 5 — Installation' },
			{
				type: 'p',
				text: 'Our installation team completes the solar installation.',
			},
			{ type: 'h', text: 'Step 6 — Inspection' },
			{ type: 'p', text: 'County inspectors verify code compliance.' },
			{ type: 'h', text: 'Step 7 — PTO' },
			{ type: 'p', text: 'Your utility grants Permission to Operate.' },
			{ type: 'h', text: 'Step 8 — Monitoring & Support' },
			{
				type: 'p',
				text: 'You begin producing power and tracking your savings — and you still have a person to call.',
			},
		],
	},
];

export function getResource(id) {
	return resources.find((r) => r.id === id);
}
